import React, { useEffect, useState } from 'react';

import { DeleteRounded, LinkRounded } from '@mui/icons-material';
import { Stack } from '@mui/material';
import _ from 'lodash';
import { useNavigate } from 'react-router';

import Breadcrumbs from '../../../components/Breadcrumbs';
import Button from '../../../components/Button';
import CommitForm from '../../../components/CommitForm';
import ContentGroupsList from '../../../components/ContentGroupsList';
import ContentWrapper from '../../../components/ContentWrapper';
import CopyButton from '../../../components/CopyButton';
import DetailsModal, { Values } from '../../../components/DetailsModal';
import Modal from '../../../components/Modal';
import NotesViewerDialog from '../../../components/NotesViewerDialog';
import SkeletonStack from '../../../components/SkeletonStack';
import {
  useDeleteNotesCategoryMutation, useDeleteNotesMutation, useGetNotesCategoryQuery,
  useNewNotesMutation, useUpdateNotesCategoryMutation, useUpdateNotesMutation,
} from '../../../services/notes';
import { Notes } from '../../../services/notes/types';
import { SUBJECTS } from '../../../utils/common/constants';
import useError from '../../../utils/react/hooks/useError';
import useSuccess from '../../../utils/react/hooks/useSuccess';
import {
  BreadcrumbsContainer,
} from '../index.styles';
import ShareButton from '../ShareButton';

const INITIAL_NOTES_FORM = {
  key: '',
  label: '',
  shortLabel: '',
  href: '',
  isExternalLink: false,
};

const INITIAL_EDIT_NOTES_FORM = _.omit(INITIAL_NOTES_FORM, ['key']);

interface CategoryPageProps {
  subject: string,
  category: string
}

type NoteRowObject = {
  row: Notes
}

const CategoryPage = ({ subject, category }: CategoryPageProps) => {
  const navigate = useNavigate();

  const { data: notesCategory, isLoading } = useGetNotesCategoryQuery(
    { subject, category }, { skip: !subject || !category },
  );
  const [notesForm, setNotesForm] = useState<Values>(INITIAL_NOTES_FORM);
  const [editNotesForm, setEditNotesForm] = useState<Values>(INITIAL_EDIT_NOTES_FORM);
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
  const [newNotesFormOpen, setNewNotesFormOpen] = useState<boolean>(false);
  const [previewNotesOpen, setPreviewNotesOpen] = useState<boolean>(false);
  const [editFormOpen, setEditFormOpen] = useState<boolean>(false);
  const [selectedNotes, setSelectedNotes] = useState<Notes | null>(null);

  const [newNotes, {
    isSuccess: isNewNotesSuccess,
    isLoading: isNewNotesLoading,
    error: newNotesError,
    reset: resetNewNotes,
  }] = useNewNotesMutation();
  const [updateCategory, {
    isSuccess: isUpdateCategorySuccess,
    isLoading: isUpdateCategoryLoading,
    isError: isUpdateCategoryError,
    error: updateCategoryError,
    reset: resetUpdateCategory,
  }] = useUpdateNotesCategoryMutation();
  const [updateNotes, {
    isSuccess: isUpdateNotesSuccess,
    isError: isUpdateNotesError,
    error: updateNotesError,
    isLoading: isUpdateNotesLoading,
  }] = useUpdateNotesMutation();
  const [deleteCategory, {
    isSuccess: isDeleteCategorySuccess,
    isError: isDeleteCategoryError,
    isLoading: isDeleteCategoryLoading,
    error: deleteCategoryError,
  }] = useDeleteNotesCategoryMutation();
  const [deleteNotes, {
    isSuccess: isDeleteNotesSuccess,
    isLoading: isDeleteNotesLoading,
    isError: isDeleteNotesError,
    error: deleteNotesError,
  }] = useDeleteNotesMutation();

  const resetModal = async () => {
    await resetNewNotes();
    setNotesForm(INITIAL_NOTES_FORM);
    await (resetUpdateCategory);
  };

  const onNoteEntryCreate = (values?: Values) => {
    newNotes({
      subject,
      category,
      attributes: values,
    });
  };

  useEffect(() => {
    if (isDeleteCategorySuccess) {
      // Navigate up a level after a category is deleted
      navigate(`/admin/notes/${subject}`);
    }
  }, [isDeleteCategorySuccess]);

  useEffect(() => {
    if (isDeleteNotesSuccess || isUpdateNotesSuccess) setEditFormOpen(false);
  }, [isDeleteNotesSuccess, isUpdateNotesSuccess]);

  useEffect(() => {
    if (selectedNotes) {
      setEditNotesForm({
        ...INITIAL_EDIT_NOTES_FORM,
        ..._.pick(selectedNotes, Object.keys(INITIAL_EDIT_NOTES_FORM)),
      });
    }
  }, [selectedNotes]);

  const onPreviewOpen = (notes: Notes) => {
    setSelectedNotes(notes);
    if (notes.isExternalLink) {
      window.open(notes.href, '_blank');
    } else {
      setPreviewNotesOpen(true);
    }
  };

  // Close modal after notes are created
  useEffect(() => {
    if (isNewNotesSuccess) {
      setNewNotesFormOpen(false);
      resetModal();
    }
  }, [isNewNotesSuccess]);

  useSuccess(isDeleteCategorySuccess, 'Category deleted');
  useSuccess(isDeleteNotesSuccess, 'Note deleted');

  useError(isUpdateCategoryError, updateCategoryError);
  useError(isDeleteCategoryError, deleteCategoryError);
  useError(isDeleteNotesError, deleteNotesError);
  useError(isUpdateNotesError, updateNotesError);

  return (
    <div>
      {!isLoading && notesCategory?.data ? (
        <>
          <BreadcrumbsContainer>
            <Breadcrumbs
              title={notesCategory?.data.shortLabel}
              path={[
                {
                  label: SUBJECTS[notesCategory?.data.subject]?.label || 'Notes',
                  href: `/admin/notes/${notesCategory?.data.subject}`,
                },
              ]}
            />
          </BreadcrumbsContainer>
          <ContentWrapper title="Category Details">
            <CommitForm
              data={notesCategory?.data}
              metadata={notesCategory?.metadata}
              fields={{
                category: {
                  xs: 12,
                  md: 12,
                },
                label: {
                  xs: 12,
                  md: 12,
                },
                shortLabel: {
                  xs: 12,
                  md: 12,
                },
              }}
              onSubmit={async (values) => updateCategory({
                subject,
                category,
                attributes: values,
              })}
              isSubmitLoading={isUpdateCategoryLoading}
              autoSubmit
            />
          </ContentWrapper>
          <ContentWrapper title="Actions">
            <Stack direction="row" spacing={1}>
              <Button
                color="error"
                size="medium"
                onClick={() => setDeleteModalOpen(true)}
                disabled={notesCategory?.data.notes.length > 0}
                startIcon={(<DeleteRounded />)}
              >
                Delete
              </Button>
              <ShareButton
                targetTitle={notesCategory?.data.shortLabel}
                subject={subject}
                category={category}
              />
              <CopyButton
                size="medium"
                copyText={`${window.location.origin}/notes?subject=${subject}&category=${category}`}
                startIcon={(<LinkRounded />)}
                buttonLabel="Copy Link"
              />
            </Stack>
          </ContentWrapper>
          <ContentWrapper title="Notes">
            <ContentGroupsList
              columns={[
                {
                  field: 'key',
                  headerName: 'Key',
                  width: 120,
                },
                {
                  field: 'label',
                  headerName: 'Note',
                  width: 360,
                },
                {
                  field: 'actions',
                  headerName: 'Actions',
                  width: 260,
                  renderCell: (props: NoteRowObject) => (
                    <Stack direction="row" spacing={1}>
                      <Button
                        size="small"
                        variant="text"
                        color="warning"
                        allCaps
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();

                          onPreviewOpen(props.row);
                        }}
                      >
                        Preview
                      </Button>
                      <CopyButton
                        size="small"
                        copyText={`${window.location.origin}/notes?subject=${subject}&category=${category}&note=${props.row.key}`}
                        buttonLabel="Copy Link"
                        variant="text"
                        allCaps
                      />
                      <ShareButton
                        targetTitle={
                          `${notesCategory?.data.shortLabel} ${props.row.label}`
                        }
                        subject={subject}
                        category={category}
                        noteKey={props.row.key}
                        hideIcon
                        size="small"
                        variant="text"
                        allCaps
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                      />
                    </Stack>
                  ),
                },
              ]}
              data={(notesCategory?.data?.notes || []).map((note) => ({
                ...note,
                id: note.noteId,
              }))}
              loading={isLoading}
              actions={{
                create: {
                  fn: () => setNewNotesFormOpen(true),
                  label: 'Add Notes',
                },
                reorder: {
                  fn: async (fields) => updateCategory({
                    subject,
                    category,
                    attributes: {
                      notes: fields?.orderedData.map((x: Notes) => x.noteId),
                    },
                  }),
                  label: 'Reorder',
                  isSuccess: isUpdateCategorySuccess,
                  isLoading: isUpdateCategoryLoading,
                  error: updateCategoryError,
                },
              }}
              onRowClick={({ row } : { row: Notes }) => {
                setSelectedNotes(row);
                setEditFormOpen(true);
              }}
            />
          </ContentWrapper>
          <DetailsModal
            open={newNotesFormOpen}
            handleClose={() => setNewNotesFormOpen(false)}
            error={newNotesError}
            title="Add New Note"
            fields={{
              key: {
                editable: true,
                type: 'text',
                label: '4-Char Key',
              },
              label: {
                editable: true,
                type: 'text',
              },
              shortLabel: {
                editable: true,
                type: 'text',
              },
              href: {
                editable: true,
                type: 'text',
                label: 'Note URL',
              },
              isExternalLink: {
                editable: true,
                type: 'boolean',
                label: 'Open in New Tab',
              },
            }}
            values={notesForm}
            buttons={[{
              fn: onNoteEntryCreate,
              label: 'Add',
              loading: isNewNotesLoading,
            }]}
          />
          <Modal
            open={deleteModalOpen}
            handleClose={() => setDeleteModalOpen(false)}
            title="Are you sure?"
            description="This will permanently delete this category."
            buttons={[
              {
                label: 'Delete',
                onClick: () => deleteCategory({ subject, category }),
                color: 'error',
                loading: isDeleteCategoryLoading,
              },
            ]}
          />
          <NotesViewerDialog
            open={previewNotesOpen}
            handleClose={() => setPreviewNotesOpen(false)}
            href={selectedNotes?.href || ''}
            label={selectedNotes?.label || 'Cognito Resource'}
            shortLabel={selectedNotes?.shortLabel || 'Resource'}
          />
          <DetailsModal
            title="Edit Note"
            subtitle={selectedNotes?.key || ''}
            open={editFormOpen}
            handleClose={() => setEditFormOpen(false)}
            fields={{
              label: {
                editable: true,
                type: 'text',
              },
              shortLabel: {
                editable: true,
                type: 'text',
              },
              href: {
                editable: true,
                type: 'text',
                label: 'Note URL',
              },
              isExternalLink: {
                editable: true,
                type: 'boolean',
                label: 'Open in New Tab',
              },
            }}
            values={editNotesForm}
            loading={false}
            buttons={[
              {
                fn: () => deleteNotes({
                  subject,
                  category,
                  noteId: selectedNotes?.noteId || '',
                }),
                label: 'Delete',
                color: 'error',
                loading: isDeleteNotesLoading,
              },
              {
                fn: (values?: Values, newValues?: Values) => {
                  updateNotes({
                    subject, category, noteId: selectedNotes?.noteId || '', attributes: newValues,
                  });
                },
                label: 'Save',
                variant: 'contained',
                loading: isUpdateNotesLoading,
                color: 'success',
                disabled: (newValues?: Values) => Object.keys(newValues || {}).length === 0,
              },
            ]}
          />
        </>
      ) : (
        <SkeletonStack />
      )}
    </div>
  );
};

export default CategoryPage;
